// Content ย้อนหลังตามช่วงที่เลือก: รูป + ยอด engagement ของโพสต์เพจ
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { fmtCompact, fmtInt } from "@shared/lib/format";
import { fbPageIds, fetchContentPosts, overviewKeys, type ContentPost } from "../api/overview-api";
import { OvLoading, OvNote } from "../components/ov-states";

/** ยอดจาก Facebook มาได้หลายชื่อ ขึ้นกับ endpoint ที่ปลายทางใช้ — ลองตามลำดับ */
function num(p: ContentPost, ...keys: string[]): number {
  for (const k of keys) {
    const v = k.split(".").reduce<unknown>((o, part) => {
      if (o == null || typeof o !== "object") return undefined;
      return (o as Record<string, unknown>)[part];
    }, p);
    if (v != null && v !== "") return Number(v) || 0;
  }
  return 0;
}

const postDate = (p: ContentPost) => {
  const raw = p.created_time || p.createdTime || p.posted_at;
  const d = new Date(String(raw ?? 0));
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
};

export function ContentBlock({ brand, days }: { brand: Brand; days: number }) {
  const ids = fbPageIds(brand);
  const hasToken = !!wazzupToken();
  const { data, isPending } = useQuery({
    queryKey: overviewKeys.content(ids, days),
    queryFn: () => fetchContentPosts(ids, days),
    enabled: hasToken && ids.length > 0,
  });

  if (!hasToken) return <OvNote>ต้องเข้าสู่ระบบ Wazzup ก่อน</OvNote>;
  if (!ids.length) return <OvNote>แบรนด์นี้ยังไม่ได้ผูกเพจ Facebook</OvNote>;
  if (isPending) return <OvLoading />;

  const posts = [...(data ?? [])].sort(
    (a, b) =>
      new Date(String(b.created_time ?? 0)).getTime() -
      new Date(String(a.created_time ?? 0)).getTime(),
  );
  if (!posts.length) return <OvNote>ไม่มีโพสต์ใน {days} วันที่ผ่านมา</OvNote>;

  return (
    <>
      <div className="ov-posts">
        {posts.slice(0, 12).map((p, i) => {
          const img = p.full_picture || p.picture || p.thumbnail || "";
          const text = String(p.message || p.caption || "(ไม่มีข้อความ)").slice(0, 90);
          const reach = num(p, "reach", "impressions", "post_impressions_unique");
          const eng =
            num(p, "likes", "reactions", "like_count") +
            num(p, "comments", "comment_count", "commentsCount") +
            num(p, "shares", "share_count");
          const rate = reach ? (eng / reach) * 100 : null;
          return (
            <a
              key={String(p.permalink_url || p.permalink || i)}
              className="ov-post"
              href={String(p.permalink_url || p.permalink || "#")}
              target="_blank"
              rel="noopener"
            >
              <span className="ov-post-img">
                {img ? (
                  <img
                    src={String(img)}
                    alt=""
                    loading="lazy"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <span className="ov-post-ph">ไม่มีรูป</span>
                )}
              </span>
              <span className="ov-post-body">
                <span className="ov-post-date muted">
                  {postDate(p)}
                  {p.pageName ? " · " + p.pageName : ""}
                </span>
                <span className="ov-post-text">{text}</span>
                <span className="ov-post-stats">
                  <b>{fmtCompact(eng)}</b> engagement
                  {reach ? ` · reach ${fmtCompact(reach)}` : ""}
                  {rate == null ? "" : ` · ${rate.toFixed(1)}%`}
                </span>
              </span>
            </a>
          );
        })}
      </div>
      {posts.length > 12 && (
        <p className="muted small-note">แสดง 12 จาก {fmtInt(posts.length)} โพสต์</p>
      )}
    </>
  );
}
